import {useState, useEffect} from "react"
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TextField, IconButton, Chip, Avatar, TablePagination, MenuItem, Select,
    FormControl, InputLabel, Tabs, Tab, Button, InputAdornment, Tooltip, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Divider,
} from "@mui/material"
import {
    Search as SearchIcon, Refresh as RefreshIcon, Visibility as VisibilityIcon,
    Block as BlockIcon, CheckCircle as CheckCircleIcon, Storefront as StorefrontIcon,
    LocalPharmacy as LocalPharmacyIcon, LocationOn as LocationOnIcon, Phone as PhoneIcon,
    Email as EmailIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import axiosInstance from "../../config/axios-config";
import {useNavigate} from "react-router-dom";

export default function TabAdminDistributorsPharmacies({tabValue}) {
    const {t} = useTranslation()
    const [subTab, setSubTab] = useState(0)
    const [tenants, setTenants] = useState([])
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [totalElements, setTotalElements] = useState(0)
    const [searchQuery, setSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState("ALL")
    const [detailDialog, setDetailDialog] = useState(false)
    const [selectedTenant, setSelectedTenant] = useState(null)

    const tenantType = subTab === 0 ? "IMPORTER" : "PHARMACY"

    const fetchTenants = async () => {
        setLoading(true)
        try {
            const response = await axiosInstance.get(`/api/v1/tenants`, {
                params: {tenantType: tenantType}
            });
            setTenants(response.data)
        } catch (error) {
            console.error("Error fetching tenants:", error)
        }
        setLoading(false)
    }

    useEffect(() => {
        if (tabValue === 1) {
            setPage(0)
            fetchTenants().then(r => {
                console.log(r)
            })
        }
    }, [tabValue, subTab, statusFilter])

    useEffect(() => {
        if (tabValue === 1) 
            fetchTenants().then(r => {
                console.log(r)
            })
    }, [page, rowsPerPage])

    const handleSearch = () => {
        setPage(0)
        fetchTenants().then(r => {
            console.log(r)
        })
    }

    const onProductNameClick = (tenantId) => {
        if (tenantType === "PHARMACY") {
            navigate(`/pharmacies/pharmacy/${tenantId}`)
        } else if (tenantType === "IMPORTER") {
            navigate(`/distributors/distributor/${tenantId}`)
        }
    }

    const handleToggleStatus = async (tenantId, currentStatus) => {
        try {
            await axiosInstance.put(`/api/v1/admin/tenants/${tenantId}/toggle-status`)
            fetchTenants()
        } catch (error) {
            console.error("Error toggling tenant status:", error)
        }
    }

    const handleViewDetails = async (tenant) => {
        setSelectedTenant(tenant)
        setDetailDialog(true)
    }

    const addressString = (tenant) => {
        if (!tenant?.address) return "-"
        return `${tenant.address.street || ""}, ${tenant.address.city || ""}, ${tenant.address.country || ""}`
    }

    const phoneString = (tenant) => {
        if (!tenant?.phones || tenant.phones.length === 0) return "-"
        return tenant.phones.map(p => `${p.phoneNumberCode} ${p.phoneNumber}`).join(", ")
    }

    if (tabValue !== 1) return null

    return (
        <Box>
            {/* Sub Tabs */}
            <Tabs value={subTab} onChange={(e, v) => setSubTab(v)} sx={{mb: 2}}>
                <Tab icon={<StorefrontIcon/>} iconPosition="start" label={t("Distributors") || "Distributors"}/>
                <Tab icon={<LocalPharmacyIcon/>} iconPosition="start" label={t("Pharmacies") || "Pharmacies"}/>
            </Tabs>

            {/* Filters */}
            <Box sx={{display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center"}}>
                <TextField
                    size="small"
                    placeholder={t("SearchByName") || "Search by name..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>,
                    }}
                    sx={{minWidth: 250}}
                />
                <FormControl size="small" sx={{minWidth: 140}}>
                    <InputLabel>{t("Status") || "Status"}</InputLabel>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label={t("Status") || "Status"}>
                        <MenuItem value="ALL">{t("All") || "All"}</MenuItem>
                        <MenuItem value="ACTIVE">{t("Active") || "Active"}</MenuItem>
                        <MenuItem value="BLOCKED">{t("Blocked") || "Blocked"}</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{flex: 1}}/>
                <Tooltip title={t("Refresh") || "Refresh"}>
                    <IconButton onClick={fetchTenants}><RefreshIcon/></IconButton>
                </Tooltip>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} elevation={1}>
                <Table>
                    <TableHead>
                        <TableRow sx={{bgcolor: "grey.50"}}>
                            <TableCell>{t("Name") || "Name"}</TableCell>
                            <TableCell>{t("Email") || "Email"}</TableCell>
                            <TableCell>{t("Address") || "Address"}</TableCell>
                            <TableCell>{t("Phone") || "Phone"}</TableCell>
                            <TableCell>{t("Status") || "Status"}</TableCell>
                            <TableCell align="center">{t("Actions") || "Actions"}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                    <Typography color="text.secondary">{t("Loading") || "Loading..."}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : tenants.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                    <Typography color="text.secondary">{t("NoDataFound") || "No data found"}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            tenants.map((tenant) => (
                                <TableRow key={tenant.id} hover>
                                    <TableCell>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer"}}
                                             onClick={() => onProductNameClick(tenant.id)}>
                                            <Avatar sx={{
                                                bgcolor: subTab === 0 ? "primary.main" : "success.main",
                                                width: 36,
                                                height: 36
                                            }}>
                                                {subTab === 0 ? <StorefrontIcon fontSize="small"/> :
                                                    <LocalPharmacyIcon fontSize="small"/>}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={500}>
                                                {tenant.tenantNameEn}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{tenant.tenantEmail || "-"}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                            {addressString(tenant)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{phoneString(tenant)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={tenant.status === "ACTIVE" ? <CheckCircleIcon /> : <BlockIcon />}
                                            label={tenant.status === "ACTIVE" ? t("Active") || "Active" : t("Blocked") || "Blocked"}
                                            color={tenant.status === "ACTIVE" ? "success" : "error"}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={t("ViewDetails") || "View Details"}>
                                            <IconButton size="small" onClick={() => handleViewDetails(tenant)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={tenant.status === "ACTIVE" ? t("Block") || "Block" : t("Unblock") || "Unblock"}>
                                            <IconButton size="small" color={tenant.status === "ACTIVE" ? "warning" : "success"} onClick={() => handleToggleStatus(tenant.id, tenant.status)}>
                                                {tenant.status === "ACTIVE" ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    labelRowsPerPage={t("RowsPerPage") || "Rows per page"}
                />
            </TableContainer>

            {/* Detail Dialog */}
            <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                        <Avatar sx={{bgcolor: subTab === 0 ? "primary.main" : "success.main"}}>
                            {subTab === 0 ? <StorefrontIcon /> : <LocalPharmacyIcon />}
                        </Avatar>
                        <Typography variant="h6">{selectedTenant?.tenantNameEn}</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedTenant && (
                        <Grid container spacing={2} sx={{mt: 1}}>
                            <Grid item xs={12}><Divider>{t("ContactInfo") || "Contact Information"}</Divider></Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    <EmailIcon color="action" />
                                    <Typography>{selectedTenant.tenantEmail || "-"}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    <PhoneIcon color="action" />
                                    <Typography>{phoneString(selectedTenant)}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    <LocationOnIcon color="action" />
                                    <Typography>{addressString(selectedTenant)}</Typography>
                                </Box>
                            </Grid>
                            {selectedTenant.description && (
                                <>
                                    <Grid item xs={12}><Divider>{t("Description") || "Description"}</Divider></Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">{selectedTenant.description}</Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog(false)}>{t("Close") || "Close"}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
